import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DASHBOARD_COLORS, sectionCardSx, sectionTitleSx } from './dashboardStyles';

const sourceChipSx = { height: 22, fontSize: 11, fontWeight: 600, color: '#1DA8A3', bgcolor: '#F0FDFA', border: '1px solid #B6DAD7' };

// "Risk and alerts" card, top right of the dashboard as in the approved design.
// Risk flags come from the latest MindVault chat and the alert from the latest
// scan; without a MindVault connection the card says what is missing.
export default function MindVaultAlertsBanner({ connected = true, latestSession, latestRiskSignals = [], latestRiskStyle, vitalSummary }) {
  const hasChatRisk = connected && latestRiskSignals.length > 0;
  const vitalAlert = connected && vitalSummary && (vitalSummary.critical.length || vitalSummary.abnormal.length || vitalSummary.stressCritical)
    ? vitalSummary.statusSummary : null;

  return (
    <Paper elevation={0} sx={{ ...sectionCardSx, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }} role="region" aria-label="Risk and alerts">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={sectionTitleSx}>Risk and alerts</Typography>
        <Chip label="From MindVault" size="small" sx={sourceChipSx} />
      </Stack>
      {!connected ? (
        <Typography variant="body2" sx={{ color: DASHBOARD_COLORS.muted }}>
          Connect this client's MindVault to see risk flags from their chats and vital signs that need attention.
        </Typography>
      ) : hasChatRisk ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.75, borderRadius: 1.5, bgcolor: latestRiskStyle.bg, border: `1px solid ${latestRiskStyle.border}` }}>
          <WarningAmberIcon sx={{ color: latestRiskStyle.text, fontSize: 20, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: latestRiskStyle.text }}>
              Risk flags in the latest MindVault chat{latestSession?.risk_level ? ` (${String(latestSession.risk_level).toLowerCase()} risk)` : ''}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {latestRiskSignals.map((flag, index) => (
                <Chip key={`${flag}-${index}`} label={flag} size="small"
                  sx={{ bgcolor: '#FFFFFF', color: latestRiskStyle.text, border: `1px solid ${latestRiskStyle.border}`, fontWeight: 600 }} />
              ))}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.75, borderRadius: 1.5, bgcolor: '#ECFDF5' }}>
          <ShieldOutlinedIcon sx={{ color: '#047857', fontSize: 20, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#065F46' }}>No active risk flags</Typography>
            <Typography sx={{ fontSize: 13, color: '#065F46' }}>No risk signals in the latest MindVault chat.</Typography>
          </Box>
        </Box>
      )}
      {vitalAlert && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, borderRadius: 1.5, bgcolor: '#FFFBF3', border: '1px solid #FDE7C7' }}>
          <WarningAmberIcon sx={{ color: '#B45309', fontSize: 20 }} />
          <Typography sx={{ fontSize: 14, color: '#78350F' }}>
            <strong>Latest scan:</strong> {vitalAlert}{vitalSummary.stressCritical ? ` Stress level is ${String(vitalSummary.stressLevel).toLowerCase()}.` : ''}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
